import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "searchInput", "countryItem", "selectedCount", "fieldsContainer", "trigger", "triggerDefaultText", "tinInfoTriggerTemplate", "tinInfoPanelTemplate"]
  static values = {
    selected: Array
  }

  connect() {
    this.selectedValues = this.selectedValue || []
    this.associationName = this.element.dataset.associationName
    this.countryType = this.element.dataset.countryType || 'citizenship'
    this.modelName = this.element.dataset.modelName
    this.tinField = this.element.dataset.tinField === 'true'
    this.tinLabelTemplate = this.element.dataset.tinLabelTemplate || ''
    this.tinPlaceholder = this.element.dataset.tinPlaceholder || ''
    this.tinInfo = this.hasTinInfoTriggerTemplateTarget && this.hasTinInfoPanelTemplateTarget
    this.tinInfoTriggerTemplate = this.tinInfo ? this.tinInfoTriggerTemplateTarget.innerHTML : ''
    this.tinInfoPanelTemplate = this.tinInfo ? this.tinInfoPanelTemplateTarget.innerHTML : ''
    this.updateSelectedCount()
    this.updateTriggerText()

    // Check all checkboxes that should be selected initially
    this.selectedValues.forEach(value => {
      const checkbox = this.element.querySelector(`input[type="checkbox"][value="${value}"]`)
      if (checkbox) {
        checkbox.checked = true
      }
    })
  }

  openModal() {
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    document.body.style.overflow = "hidden"

    // Focus search input
    setTimeout(() => {
      this.searchInputTarget.focus()
    }, 100)
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    document.body.style.overflow = ""
  }

  toggleCountry(event) {
    const checkbox = event.currentTarget
    const value = checkbox.value

    if (checkbox.checked) {
      if (!this.selectedValues.includes(value)) {
        this.selectedValues.push(value)
        const item = checkbox.closest('[data-country-multiselect-target="countryItem"]')
        const countryName = (item && item.dataset.countryName) || value
        this.addCountryField(value, countryName)
      }
    } else {
      this.selectedValues = this.selectedValues.filter(v => v !== value)
      this.removeCountryField(value)
    }

    this.updateSelectedCount()
    this.updateTriggerText()
  }

  addCountryField(countryCode, countryName = countryCode) {
    // Check if field already exists
    const existing = this.fieldsContainerTarget.querySelector(`[data-country-code="${countryCode}"]`)
    if (existing) {
      this.reviveCountryField(existing)
      return
    }

    // Create a new nested field group
    const index = new Date().getTime() // Use timestamp as unique index
    const fieldGroup = document.createElement('div')
    fieldGroup.dataset.countryCode = countryCode
    const inputPrefix = `${this.modelName}[${this.associationName}_attributes][${index}]`

    // Create hidden fields for country entry
    let markup = `
      <input type="hidden" name="${inputPrefix}[country_code]" value="${countryCode}">
      <input type="hidden" name="${inputPrefix}[country_type]" value="${this.countryType}">
    `

    // For taxation-related entries, also collect a required TIN per country —
    // but only for foreign countries; a Finnish TIN is never asked for.
    if (this.tinField && countryCode !== 'FI') {
      const labelText = this.escapeHtml(this.tinLabelTemplate.replace('{country}', countryName))
      const tinId = `tin_${this.associationName}_${index}_${countryCode}`
      markup += `
        <div class="w-full rounded-md px-4 h-14 flex flex-col justify-center lg:px-6 lg:h-16 bg-white cursor-text focus-within:ring-[3px] focus-within:ring-focus focus-within:ring-offset-0 transition-colors duration-normal mt-2">
          <div class="flex items-center gap-1">
            <label for="${tinId}" class="type-body-s mb-0 block text-text-placeholder cursor-text">${labelText}</label>
            ${this.tinInfoMarkup(this.tinInfoTriggerTemplate, tinId)}
          </div>
          <input type="text" id="${tinId}" name="${inputPrefix}[tin]" required placeholder="${this.escapeHtml(this.tinPlaceholder)}" class="w-full bg-transparent type-body-m placeholder:text-text-placeholder outline-none text-text-primary focus:outline-none" data-form-validator-target="field">
        </div>
        ${this.tinInfoMarkup(this.tinInfoPanelTemplate, tinId)}
      `
      // The info-drawer controller pairs the icon by the label with the panel under the box.
      if (this.tinInfo) fieldGroup.dataset.controller = 'info-drawer'
    }

    fieldGroup.innerHTML = markup

    this.fieldsContainerTarget.appendChild(fieldGroup)
  }

  // Trigger or panel markup of the TIN info drawer, with an id unique to this TIN field.
  tinInfoMarkup(template, tinId) {
    return template.replaceAll('{panel_id}', `${tinId}_info`)
  }

  escapeHtml(value) {
    const div = document.createElement('div')
    div.textContent = value
    return div.innerHTML
  }

  removeCountryField(countryCode) {
    const fieldGroup = this.fieldsContainerTarget.querySelector(`[data-country-code="${countryCode}"]`)
    if (fieldGroup) {
      // Check if this is a persisted record (has an ID field)
      const idField = fieldGroup.querySelector('input[name$="[id]"]')
      if (idField) {
        // Mark for destruction instead of removing. The group has to stay in the DOM so the
        // id and _destroy fields are submitted, but its visible inputs must not: a `required`
        // TIN box left behind for a country the user just deselected would block the whole
        // form from submitting, with the browser focusing a field that is no longer shown.
        const destroyField = document.createElement('input')
        destroyField.type = 'hidden'
        destroyField.name = idField.name.replace('[id]', '[_destroy]')
        destroyField.value = '1'
        fieldGroup.appendChild(destroyField)
        this.setFieldGroupHidden(fieldGroup, true)
      } else {
        // Remove the field group entirely if it's not persisted
        fieldGroup.remove()
      }
    }
  }

  // Undo removeCountryField when the user re-selects a country they had deselected.
  reviveCountryField(fieldGroup) {
    const destroyField = fieldGroup.querySelector('input[name$="[_destroy]"]')
    if (!destroyField) return

    destroyField.remove()
    this.setFieldGroupHidden(fieldGroup, false)
  }

  // Hidden inputs (id, country_code, country_type, _destroy) always stay submittable;
  // everything the user sees is hidden and disabled, which both takes it out of the
  // submitted params and exempts it from native `required` validation.
  setFieldGroupHidden(fieldGroup, hidden) {
    fieldGroup.hidden = hidden
    fieldGroup.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach(input => {
      input.disabled = hidden
    })
  }

  search(event) {
    const query = event.target.value.toLowerCase()

    this.countryItemTargets.forEach(item => {
      const countryName = item.dataset.countryName.toLowerCase()
      if (countryName.includes(query)) {
        item.classList.remove("hidden")
      } else {
        item.classList.add("hidden")
      }
    })
  }

  confirm(event) {
    event.preventDefault()
    this.closeModal()
  }

  updateSelectedCount() {
    const count = this.selectedValues.length
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count
    }
  }

  updateTriggerText() {
    if (this.hasTriggerTarget) {
      const count = this.selectedValues.length
      if (count === 0) {
        this.triggerTarget.textContent = this.triggerDefaultTextTarget.textContent
      } else if (count <= 3) {
        // Find the country name from the checked checkbox
        let countryNames = [];
        this.selectedValues.forEach(value => {
          const checkbox = this.element.querySelector(`input[type="checkbox"][value="${value}"]`)
          if (checkbox) {
            const item = checkbox.closest('[data-country-multiselect-target="countryItem"]')
            if (item && item.dataset.countryName) {
              countryNames.push(item.dataset.countryName)
            }
          }
        })
        this.triggerTarget.textContent = countryNames.join(", ")
      } else {
        this.triggerTarget.textContent = `${count} maata valittu`
      }
    }
  }

  // Close modal when clicking outside
  handleBackdropClick(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }
}
